import ButtonHelpTooltip from "components/Tooltip/ButtonHelpTooltip";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { CommonText } from "styledComponents/common";
import { formatCompletionTime } from "utils/helper";

import audioIcon from "../../../assets/img/icons/audio-gallery-primary.svg";
import dashboardStatIconWhite from "../../../assets/img/icons/dashboard-stat-icon-white.svg";
import { TodaysAttendanceHeader, TodaysAttendanceIconButton, TodaysAttendanceTitle, TopPerformersContainer, TopPerformersListContainer } from "../../DashBoardNew/dashBoardNewStyles";
import EvidenceDisplay from "../../../components/EvidenceDisplay";


const SectionHeaderButton = styled(TodaysAttendanceIconButton)`
  cursor: pointer;
`;

const ListWrapper = styled(TopPerformersListContainer)`
  gap: 9px;
`;

const RowCard = styled.div`
  display: grid;
  grid-template-columns: minmax(60px, 350px) minmax(60px, 200px) minmax(
      100px,
      120px
    ) 32px;
  gap: 9px;
  align-items: center;
  width: 100%;
  /* padding: 12px; */
  /* border: 1px solid #d3e0fc; */
  border-radius: 8px;
  padding-left: 2px;
  background: #ffffff;
  box-sizing: border-box;
`;

const ColumnBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const JobNameValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #000e2a;
  /* word-break: break-word; */

  text-transform: capitalize;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const CompletionTimeValue = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #353a46;
  word-break: break-word;
`;

const DetailsButtonWrap = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const RowActionButton = styled.button`
  border: none;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  padding: 0;
  border-radius: 4px;
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 2fr) minmax(140px, 1.2fr) minmax(
      220px,
      2fr
    ) 32px;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 12px;
  border: 1px solid #e6eefc;
  border-radius: 8px;
  background: #ffffff;

  @media screen and (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonBlock = styled.div`
  height: 14px;
  width: ${({ width }) => width || "100%"};
  border-radius: 999px;
  background: linear-gradient(90deg, #eef3fb 0%, #dae4f4 50%, #eef3fb 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const formatRowValue = value => {
  if (value === null || value === undefined || value === "") {
    return "----";
  }

  return value;
};

const getPlaceholderRow = index => ({
  job_id: `placeholder-${index}`,
  job_name: null,
  completion_time: null,
  evidence: [],
});

const RecentCompletedJobRow = ({
  job,
  timezone,
  onOpenDetails,
}) => {
  const isPlaceholderRow = String(job?.job_id || "").startsWith("placeholder-");
  const completionTime = formatCompletionTime(job?.completion_time, timezone);
  const hasCompletionTime = completionTime && completionTime !== "-";

  return (
    <RowCard>
      <ColumnBlock>
        <JobNameValue>{formatRowValue(job?.job_name)}</JobNameValue>
      </ColumnBlock>

      <ColumnBlock>
        <CompletionTimeValue>
          {hasCompletionTime ? completionTime : "----"}
        </CompletionTimeValue>
      </ColumnBlock>

      <ColumnBlock>
        <EvidenceDisplay
          evidence={job?.evidence}
          height="32px"
          width="26px"
          showGallery={!isPlaceholderRow}
          showAudioDuration={false}
        />
      </ColumnBlock>

      <DetailsButtonWrap>
        <RowActionButton
          type="button"
          disabled={isPlaceholderRow}
          onClick={isPlaceholderRow ? undefined : onOpenDetails}
          aria-label="Open job details"
        >
          <img src={dashboardStatIconWhite} alt="Open details" />
        </RowActionButton>
      </DetailsButtonWrap>
    </RowCard>
  );
};

const RecentCompletedJobsSection = ({
  history,
  selectedOrganization,
  getDashboardRecentCompletedJobs,
  recentCompletedJobsIsLoading,
  recentCompletedJobsData,
}) => {
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const sectionTitle = isOperationType
    ? "Recent completed tasks"
    : "Recent completed jobs";

  useEffect(() => {
    if (selectedOrganization?.id && getDashboardRecentCompletedJobs) {
      getDashboardRecentCompletedJobs({
        organization_id: selectedOrganization.id,
      });
    }
  }, [selectedOrganization, getDashboardRecentCompletedJobs]);

  const handleOpenDetails = job => {
    const workOrderId = job?.work_order_id || job?.job_id;
    const jobId = job?.job_id;

    history.push(
      `/user/task-details?job_id=${encodeURIComponent(
        workOrderId || "",
      )}&task_id=${encodeURIComponent(
        workOrderId || "",
      )}&id=${encodeURIComponent(jobId || "")}`,
    );
  };

  const jobs = Array.isArray(recentCompletedJobsData)
    ? recentCompletedJobsData
    : [];
  const displayJobs = Array.from(
    { length: 5 },
    (_, index) => jobs[index] || getPlaceholderRow(index),
  );
  const timezone = selectedOrganization?.timezone;

  return (
    <ButtonHelpTooltip
      transform="translateX(-50%) translateY(140%)"
      enabled={true}
      text="List of tasks that has been completed recently"
    >
      <TopPerformersContainer>
        <TodaysAttendanceHeader>
          <TodaysAttendanceTitle>{sectionTitle}</TodaysAttendanceTitle>
        </TodaysAttendanceHeader>

        <ListWrapper>
          {recentCompletedJobsIsLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <SkeletonRow key={`recent-job-skeleton-${index}`}>
                  <ColumnBlock>
                    <SkeletonBlock width="70%" />
                    <SkeletonBlock width="90%" />
                  </ColumnBlock>
                  <ColumnBlock>
                    <SkeletonBlock width="60%" />
                    <SkeletonBlock width="80%" />
                  </ColumnBlock>
                  <ColumnBlock>
                    <SkeletonBlock width="45%" />
                    <SkeletonBlock width="100%" height="52px" />
                  </ColumnBlock>
                  <SkeletonBlock width="32px" height="32px" />
                </SkeletonRow>
              ))
            : displayJobs.map(job => (
                <RecentCompletedJobRow
                  key={job.job_id}
                  job={job}
                  timezone={timezone}
                  onOpenDetails={() => handleOpenDetails(job)}
                />
              ))}
        </ListWrapper>
      </TopPerformersContainer>
    </ButtonHelpTooltip>
  );
};

export default RecentCompletedJobsSection;